import { ref, onMounted, watch, computed } from 'vue'

import { useConfigStore } from '@toni/rendering/src/stores/config'
import { usePageStore } from '@toni/rendering/src/stores/page'
import { useSourcepointStore } from '@toni/rendering/src/stores/sourcepoint'
import { useSessionStore } from '@toni/rendering/src/stores/session'
export function useClientScriptLoader () {
  if (import.meta.env.SSR) return

  const loadedScripts = ref([])
  const sourcepointStore = useSourcepointStore()
  const { rsConfig } = useConfigStore()
  const { pageData } = usePageStore()
  const { clientSideScripts } = rsConfig
  const hasPurSub = computed(() => useSessionStore().hasPurSub)

  const updateHeadScripts = async () => {
    if (!rsConfig || !pageData) return

    if (clientSideScripts?.length) {
      await Promise.all(clientSideScripts.map(async ({ vendor, scripts }) => {
        const vendorId = rsConfig.consent.vendor[vendor]

        if (!sourcepointStore.consentedVendors.includes(vendorId)) {
          return
        }

        for (const { ...scriptProps } of scripts) {
          const scriptAlreadyLoaded = loadedScripts.value.includes(vendorId)
          const isExcludedPageType = scriptProps?.excludeFrom === pageData.pageMeta.pageType
          const ignoreScriptForPurSub = hasPurSub.value && scriptProps?.excludeForPurUsers
          if (!ignoreScriptForPurSub && !scriptAlreadyLoaded && !isExcludedPageType) {
            await loadScript(scriptProps.src, scriptProps)
              .then(() => {
                console.debug(`Loaded ${vendorId}: ${scriptProps.src}`)
                loadedScripts.value.push(vendorId)
              })
              .catch(error => console.error(`[${vendorId}] Error loading ${vendorId}`, error))
          }
        }
      }))
    }
  }

  const loadScript = (url, properties = {}, forceProperties = false) => {
    return new Promise((resolve, reject) => {
      if (document.head.querySelector(`[src="${url}"]`)) {
        return resolve()
      }

      const script = document.createElement('script')

      script.async = true
      script.src = url
      script.onload = resolve
      script.onerror = reject

      if (forceProperties) {
        for (const [key, val] of Object.entries(properties)) { script.setAttribute(key, val) }
      } else {
        Object.assign(script, properties)
      }

      document.head.append(script)
    })
  }

  onMounted(() => {
    updateHeadScripts()
  })

  watch(sourcepointStore, (newValue) => {
    if (newValue.consentedVendors.length) {
      updateHeadScripts()
    }
  })

  return {
    loadedScripts,
    updateHeadScripts,
    loadScript
  }
}
